/**
 * MediaWriteService — atomic media write (upload → record) with rollback.
 *
 * ARCHITECTURE (BAL-657, lineage BAL-595). Media upload is a dual-write to two
 * non-transactional systems: Supabase Storage bytes + a Postgres row. With no
 * atomicity, a failure between the two leaves an orphan (object with no row, or
 * row with no object). This service is the web-TS prevention layer (Layer 1a/1b);
 * a runtime-agnostic reconciliation cron (Layer 2) is the universal net that also
 * covers the browser signed-URL PUTs and the mobile-Dart writers this TS code
 * cannot reach.
 *
 * Two entry points, one per producer shape:
 *
 *  - `uploadAndRecord` (Layer 1a, server-side `.upload()` producers): uploads the
 *    bytes, then runs the caller's `writeRow` closure. If `writeRow` fails (or
 *    throws), the just-uploaded object is deleted before returning — no orphan
 *    object (R1).
 *
 *  - `finalizeVerified` (Layer 1b, out-of-band byte upload — browser signed-URL
 *    PUT or a mobile client): the bytes were uploaded elsewhere; this verifies
 *    the object actually exists (`storage.info`) BEFORE running `writeRow`. If the
 *    object is missing, it fails and writes NO row — no orphan row (R2). This is
 *    the exact BAL-595 failure: the browser PUT never landed but finalize inserted
 *    the row anyway.
 *
 * Both take a `writeRow(verifiedPath)` closure so the caller owns the row shape,
 * table, and RLS-scoped client; the service owns only the storage↔row atomicity.
 *
 * @see ./storage-url.service.ts (signed-URL / read side)
 * @see ./r2.service.ts (public-content counterpart)
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';

import {
  extractDeniedObjectName,
  isAlwaysBugDbError,
} from '../services/db-error-classification';
import type { StorageBucket } from './storage-url.service';

/**
 * Fire a Sentry capture when a storage-write error is an "always a bug" DB
 * error (missing GRANT, schema drift) that the Result pattern would otherwise
 * swallow. No-op for ordinary storage failures (no SQLSTATE) and expected
 * authorization outcomes. Does not change the returned Result.
 */
function captureIfAlwaysBugStorageError(
  error: { code?: string; message?: string },
  step: string,
): void {
  if (!isAlwaysBugDbError(error)) return;
  captureServiceError(
    { message: error.message ?? '', code: error.code, details: error },
    {
      action: `db.${error.code}`,
      step,
      deniedObject: extractDeniedObjectName(error.message),
      sqlstate: error.code,
    },
  );
}

/**
 * Any Supabase Storage bucket. Preserves autocomplete for the known
 * `StorageBuckets` while still accepting a bucket declared as a bare literal
 * at a call site (e.g. `'organization-media'`), matching what
 * `client.storage.from()` itself accepts.
 */
export type MediaBucket = StorageBucket | (string & {});

/**
 * Bodies Supabase Storage `.upload()` accepts. Kept permissive so every web-TS
 * producer (File from a form, Blob, Buffer from a server-rendered PDF, a base64
 * string) can route through the service unchanged.
 */
export type MediaUploadBody =
  | File
  | Blob
  | ArrayBuffer
  | ArrayBufferView
  | Buffer
  | string;

export interface MediaUploadOptions {
  contentType?: string;
  /** Default false — media writes never silently overwrite an existing object. */
  upsert?: boolean;
  cacheControl?: string;
}

export interface UploadAndRecordParams<TRow> {
  bucket: MediaBucket;
  path: string;
  body: MediaUploadBody;
  uploadOptions?: MediaUploadOptions;
  /**
   * Insert/return the canonical row for `verifiedPath`. Must return a
   * `Result` — a failure (or a throw) triggers rollback of the uploaded object.
   */
  writeRow: (verifiedPath: string) => Promise<Result<TRow>>;
  /**
   * Optional: does the row for `verifiedPath` exist? Consulted only after
   * `writeRow` failed or threw. A response can time out AFTER Postgres
   * committed the row, and removing the object then leaves a row pointing at
   * nothing — the direction that renders a broken image. When this returns
   * true OR throws, the object is kept and the failure is still returned;
   * only a definite false removes the object.
   */
  rowExists?: (verifiedPath: string) => Promise<boolean>;
}

export interface FinalizeVerifiedParams<TRow> {
  bucket: MediaBucket;
  path: string;
  writeRow: (verifiedPath: string) => Promise<Result<TRow>>;
}

export class MediaWriteService {
  constructor(private readonly client: SupabaseClient) {}

  /**
   * Layer 1a — upload bytes, then record the row atomically. On row-write
   * failure the uploaded object is removed (best-effort; the reconciliation
   * cron sweeps any straggler). Returns the row on success.
   */
  async uploadAndRecord<TRow>(
    params: UploadAndRecordParams<TRow>,
  ): Promise<Result<TRow>> {
    const { bucket, path, body, uploadOptions, writeRow, rowExists } = params;

    if (!path || path.trim() === '') {
      return Result.fail(ServiceError.validation('Storage path is required'));
    }

    const { error: uploadError } = await this.client.storage
      .from(bucket)
      .upload(path, body, {
        contentType: uploadOptions?.contentType,
        upsert: uploadOptions?.upsert ?? false,
        cacheControl: uploadOptions?.cacheControl,
      });

    if (uploadError) {
      captureIfAlwaysBugStorageError(uploadError, 'media.uploadAndRecord');
      return Result.fail(
        ServiceError.database(
          `Media upload failed: ${uploadError.message}`,
          uploadError,
        ),
      );
    }

    let rowResult: Result<TRow>;
    try {
      rowResult = await writeRow(path);
    } catch (error) {
      // writeRow threw — object is orphaned unless we roll back.
      await this.rollbackUnlessRowLanded(bucket, path, rowExists);
      return Result.fail(
        ServiceError.internal(
          error instanceof Error
            ? error.message
            : 'Row write threw during media record',
          error,
        ),
      );
    }

    if (rowResult.isFailure) {
      await this.rollbackUnlessRowLanded(bucket, path, rowExists);
      return rowResult;
    }

    return rowResult;
  }

  /**
   * Remove the uploaded object after a failed row write, unless the row may
   * have landed anyway.
   *
   * The two mistakes are not symmetric. A stray object with no row is
   * harmless and the reconciliation job reports it tonight. A row whose
   * object was deleted is a broken image on a live profile, and nothing
   * repairs it: the job checks objects without rows, not rows without
   * objects. So when `rowExists` cannot answer (it threw: the same network
   * blip that lost the insert response), the object is KEPT. Only a definite
   * "no row" removes it.
   */
  private async rollbackUnlessRowLanded(
    bucket: MediaBucket,
    path: string,
    rowExists: ((verifiedPath: string) => Promise<boolean>) | undefined,
  ): Promise<void> {
    if (rowExists) {
      const landed = await rowExists(path).catch(() => true);
      if (landed) return;
    }
    await this.removeObjectQuietly(bucket, path);
  }

  /**
   * Layer 1b — the bytes were uploaded out-of-band (browser signed-URL PUT or a
   * mobile client). Verify the object exists BEFORE writing the row; if it does
   * not, fail and write no row (prevents the BAL-595 orphan-row class).
   */
  async finalizeVerified<TRow>(
    params: FinalizeVerifiedParams<TRow>,
  ): Promise<Result<TRow>> {
    const { bucket, path, writeRow } = params;

    if (!path || path.trim() === '') {
      return Result.fail(ServiceError.validation('Storage path is required'));
    }

    const exists = await this.objectExists(bucket, path);
    if (!exists) {
      return Result.fail(
        ServiceError.notFound(
          `Storage object not found; refusing to write orphan row: ${bucket}/${path}`,
          { bucket, path },
        ),
      );
    }

    return writeRow(path);
  }

  /**
   * Upload-only primitive for producers that have no owning row to write in the
   * same call (generic upload hooks, download-then-store utilities, or column
   * updates the caller performs itself). Centralizes the raw `.upload()` so no
   * production site calls `client.storage.from().upload()` directly
   * (lint-enforced, R4). Returns the stored path on success.
   *
   * When there IS a row to write in the same operation, prefer
   * `uploadAndRecord` — it adds rollback-on-row-failure. This method does NOT
   * roll back (there is nothing to roll back against); the reconciliation cron
   * (Layer 2) is the net for any object this leaves without an eventual row.
   */
  async uploadObject(
    bucket: MediaBucket,
    path: string,
    body: MediaUploadBody,
    options?: MediaUploadOptions,
  ): Promise<Result<{ path: string }>> {
    if (!path || path.trim() === '') {
      return Result.fail(ServiceError.validation('Storage path is required'));
    }

    const { data, error } = await this.client.storage
      .from(bucket)
      .upload(path, body, {
        contentType: options?.contentType,
        upsert: options?.upsert ?? false,
        cacheControl: options?.cacheControl,
      });

    if (error) {
      captureIfAlwaysBugStorageError(error, 'media.uploadObject');
      return Result.fail(
        ServiceError.database(`Media upload failed: ${error.message}`, error),
      );
    }

    return Result.ok({ path: data?.path ?? path });
  }

  /**
   * True iff an object exists at `path` in `bucket`. Uses `storage.info`, which
   * returns an error (not a throw) for a missing object.
   */
  async objectExists(bucket: MediaBucket, path: string): Promise<boolean> {
    try {
      const { data, error } = await this.client.storage.from(bucket).info(path);
      return !error && !!data;
    } catch {
      // Network/SDK error → treat as "cannot confirm"; caller fails closed.
      return false;
    }
  }

  /**
   * Bounded-retry existence check for the out-of-band signed-URL PUT path
   * (BALLEE-G7). The browser PUT to the storage signed URL and the server's
   * `.info()` read reach Supabase Storage over two independent connections;
   * under concurrent load the `storage.objects` metadata row created by the PUT
   * is not always visible to the immediately-following server `.info()` read
   * (read-after-write lag). A single miss must NOT strand a file the browser
   * has already uploaded, so re-check with short linear backoff before giving
   * up. Returns true as soon as the object is seen.
   *
   * Confirmed in production 2026-07-09: during the GDO apply burst, 300
   * dancer-media objects landed in storage but only 214 artist_media rows were
   * inserted — 87 orphan objects whose finalize checked existence exactly once
   * and failed closed. `attempts=4, delayMs=300` waits ~0/300/600/900ms
   * (≈1.8s worst case) before declaring a genuine miss.
   */
  async objectExistsWithRetry(
    bucket: MediaBucket,
    path: string,
    attempts = 4,
    delayMs = 300,
  ): Promise<boolean> {
    for (let attempt = 0; attempt < attempts; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
      }
      if (await this.objectExists(bucket, path)) {
        return true;
      }
    }
    return false;
  }

  /** Best-effort object removal used for rollback. Never throws. */
  private async removeObjectQuietly(
    bucket: MediaBucket,
    path: string,
  ): Promise<void> {
    try {
      await this.client.storage.from(bucket).remove([path]);
    } catch {
      // Reconciliation cron (Layer 2) sweeps any object we failed to remove.
    }
  }
}

/**
 * Factory — mirrors createStorageUrlService for consistency at call sites.
 */
export function createMediaWriteService(
  client: SupabaseClient,
): MediaWriteService {
  return new MediaWriteService(client);
}
